<script setup lang="ts">
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

if (import.meta.client)
  gsap.registerPlugin(ScrollTrigger)

let gsapContext: ReturnType<typeof gsap.context>

const { menu, getNavItems } = useNav()
const { breakpoints } = useScreen()

const drop = ref(false)

const modal = ref(false)

const navbarEl = ref<HTMLElement>()
const navdropEl = ref<HTMLElement>()
const navmodalEl = ref<HTMLElement>()

const { activate: startDropTrap, deactivate: stopDropTrap } = useFocusTrap(navdropEl)

const { activate: startModalTrap, deactivate: stopModalTrap } = useFocusTrap(navmodalEl, {
  initialFocus: '#close-modal',
})

const isSmall = breakpoints.smallerOrEqual('sm')

function toggleNavdrop() {
  drop.value = !drop.value
}

function closeOnNav(event: Event) {
  const element = event.target as HTMLElement
  if (!element)
    return
  element.blur()
  if (element.tagName === 'A') {
    drop.value = false
    modal.value = false
  }
}

const onNavmodal = {
  afterEnter: (el: HTMLElement) => {
    disableBodyScroll(el)
    startModalTrap()
  },

  beforeLeave: (el: HTMLElement) => {
    stopModalTrap()
    enableBodyScroll(el)
  },
}

const onNavdrop = {
  afterEnter: () => {
    startDropTrap()
  },
  beforeLeave: () => {
    stopDropTrap()
  },
}

onClickOutside(navdropEl, (event) => {
  if (!drop.value)
    return
  event.stopPropagation()
  drop.value = false
})

onKeyStroke('Escape', () => {
  drop.value = false
  modal.value = false
})

const scrollTriggerProps = {
  duration: 0.2,
  ease: 'power1.inOut',
  scrollTrigger: {
    trigger: navbarEl.value,
    start: 'top top-=180',
    toggleActions: 'play none none reverse',
  },
}

onMounted(() => {
  gsapContext = gsap.context(() => {
    if (!navbarEl.value)
      return

    const q = gsap.utils.selector(navbarEl.value)
    const grid = q('.grid')
    const logo = q('.logo')
    const menu = q('.menu')

    gsap.set(grid, {
      'column-gap': 0,
    })
    gsap.set(logo, {
      width: 0,
    })
    gsap.set(menu, {
      'margin-right': 32,
    })

    gsap.to(grid, {
      'column-gap': 'clamp(0rem, -0.9184rem + 4.0816vw, 3rem)',
      ...scrollTriggerProps,
    })
    gsap.to(logo, {
      width: 112,
      ...scrollTriggerProps,
    })
    gsap.to(menu, {
      'margin-right': 0,
      ...scrollTriggerProps,
    })
  })
})

onUnmounted(() => {
  if (gsapContext)
    gsapContext.revert()
})
</script>

<template>
  <!-- NAVMODAL -->
  <Teleport to="body">
    <Transition
      name="navmodal"
      v-on="onNavmodal"
    >
      <!-- Navmodal -->
      <div
        v-if="modal"
        ref="navmodalEl"
        class="h-screen inset-x-0 fixed z-9999 overflow-y-auto"
      >
        <div class="py-2 bg-white shadow top-0 sticky z-1 dark:bg-icsnight-950 dark:shadow-black/50">
          <BlockContent class="flex gap-6 items-center justify-between">
            <IcsLogo
              class="w-28 -m-1"
              @click="modal = false"
            />
            <!-- navmodal button -->
            <AButton
              id="close-modal"
              label="Close"
              size="fluid"
              color="red"
              icon="i-carbon-close-large"
              @click="modal = false"
            />
          </BlockContent>
        </div>
        <Navmenu
          class="py-6"
          @click="closeOnNav"
        />
      </div>
    </Transition>
  </Teleport>

  <!-- NAVBAR & NAVDROP -->
  <div class="ics-0q7lym">
    <!-- Navbar -->
    <div
      ref="navbarEl"
      class="py-2 bg-white shadow-md dark:bg-icsnight dark:shadow-black/50"
    >
      <div class="w-content grid auto-cols-[min-content_auto_min-content] grid-flow-col items-center">
        <div class="logo flex w-0 overflow-hidden -ml-1">
          <IcsLogo class="shrink-0 w-28" />
        </div>
        <div class="menu">
          <ANav
            :menu="menu"
            inline="always"
            class="w-full hidden f-max-w-672-800 sm:block"
            ul-class="!gap-x-0 justify-between"
            li-class=""
          >
            <template #default="{ item }">
              <ADropdown>
                <template #title>
                  <NuxtLink
                    class="heading-link-bright text-center focusable flex whitespace-nowrap text-truncate transition items-center f-text-10-16 f-h-32-48 f-px-12-24 f-mx--12--24 button-label media-[(pointer:coarse)]:min-h-11"
                    :to="item.to"
                    active-class="heading-link-active"
                  >
                    {{ item.label || item.title || item.name }}
                  </NuxtLink>
                </template>
                <template
                  v-if="item.children && !drop"
                  #content
                >
                  <div class="pt-5">
                    <ANav
                      :menu="getNavItems(item.children)"
                      class="py-2 rounded-xs bg-white f-pl-6-12 f-pr-12-24 dark:bg-icsnight"
                    />
                  </div>
                </template>
              </ADropdown>
            </template>
          </ANav>
        </div>
        <!-- navbar button -->
        <AButton
          :label="drop ? 'Close' : 'Menu'"
          size="fluid"
          :color="drop ? 'red' : 'white'"
          :icon="drop ? 'i-carbon-close-large' : 'i-carbon-menu'"
          @click="isSmall ? modal = true : toggleNavdrop()"
        />
      </div>
    </div>

    <!-- Navdrop -->
    <Transition
      name="navdrop"
      v-on="onNavdrop"
    >
      <Navmenu
        v-show="drop"
        ref="navdropEl"
        class="py-6 shadow-lg inset-x-0 absolute dark:shadow-black/38 -z-1"
        @click="closeOnNav"
      />
    </Transition>
  </div>
</template>

<style>
.navdrop-enter-active,
.navdrop-leave-active {
  transition-property: transform;
  transition-duration: 150ms;
}

.navdrop-enter-from,
.navdrop-leave-to {
  transform: translateY(-100%);
}

.navmodal-enter-active,
.navmodal-leave-active {
  transition-property: opacity;
  transition-duration: 150ms;
}

.navmodal-enter-from,
.navmodal-leave-to {
  opacity: 0;
}

.navdrop-enter-active {
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
}

.navdrop-leave-active {
  transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
}

.navdrop-enter-to,
.navdrop-leave-from {
  transform: translateY(0%);
}

@media (prefers-reduced-motion) {
  .navdrop-enter-active,
  .navdrop-leave-active {
    transition-property: opacity;
    transition-duration: 150ms;
  }
}
</style>
